import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"
import { t } from "../lib/i18n"
import { intersection } from "lodash"

type EditState = "view" | "edit"

export default class extends Controller<HTMLFormElement> {
  static values = { state: String }
  static targets = ["text", "input"]

  declare stateValue: EditState
  declare readonly textTarget: HTMLElement
  declare readonly inputTarget: HTMLInputElement

  connect() {
    if (this.textTarget.textContent === "") {
      this.stateValue = "edit"
    } else {
      this.stateValue = "view"
    }
  }

  edit() {
    this.stateValue = "edit"
    window.setTimeout(() => this.inputTarget.focus())
  }

  cancelEdit() {
    this.stateValue = "view"
  }

  commitEdit() {
    const trimmedValue = this.inputTarget.value.trim()

    if (!trimmedValue) {
      this.inputTarget.setCustomValidity(t("required_field"))
      this.inputTarget.reportValidity()
      this.inputTarget.focus()
      return
    }
    this.inputTarget.setCustomValidity("")

    if (!this._checkValidity()) {
      return
    }

    post(this.element.action, { body: new FormData(this.element) })

    this.textTarget.innerText = trimmedValue
    this.cancelEdit()
  }

  submit(event) {
    event.preventDefault()
    this.commitEdit()
  }

  keydown(event) {
    if (event.key === "Escape") {
      this.cancelEdit()
    } else if (event.key === "Enter") {
      if (event.target.tagName === "TEXTAREA") {
        return
      }
      this.commitEdit()
    }
  }

  windowClick(event) {
    if (
      intersection(Array.from(this.element.children), event.composedPath()).length === 0 &&
      this.stateValue === "edit"
    ) {
      this.commitEdit()
    }
  }

  _checkValidity() {
    this.element.classList.add("was-validated")
    return this.element.checkValidity()
  }

  stateValueChanged() {
    // TODO: These classes should not be used on the inline edit elements
    //       anymore because we are now setting the display property directly.
    this.textTarget.classList.remove("d-none", "hidden")
    this.inputTarget.classList.remove("d-none", "hidden")

    if (this.stateValue === "edit") {
      this.textTarget.style.display = "none"
      this.inputTarget.style.removeProperty("display")
    } else {
      this.textTarget.style.removeProperty("display")
      this.inputTarget.style.display = "none"
    }
  }
}
